import { RightOutlined } from '@ant-design/icons'
import { Button, Flex, Typography } from 'antd'
import dayjs from 'dayjs'
import React from 'react'
import { useRecoilState } from 'recoil'
import Swal from 'sweetalert2'
import { deleteApproval } from '../../../api/electronicApproval.api'
import { userState } from '../../../store/user'
import '../electricApproval.scss'

const { Text } = Typography

const ApprovalProcessForChief = ({
  proposalList,
  popupMode,
  setPopupMode,
  isChief,
  setSelectedApprovalId,
  setShowDetailPopup,
  refetchApprovalList,
  setSelectedApprovalStatus,
}) => {
  const [user] = useRecoilState(userState)

  // 상태 정의
  const statuses = [
    {
      key: 'PENDING',
      label: '승인대기',
      emptyMessage: '승인 대기 기안서가 없습니다.',
    },
    {
      key: 'APPROVED',
      label: '승인 완료',
      emptyMessage: '승인완료된 기안서가 없습니다.',
    },
    {
      key: 'ON_HOLD',
      label: '승인 보류',
      emptyMessage: '승인보류 된 기안서가 없습니다.',
    },
  ]

  // 스튜디오 목록 (하우투약 제외)
  const studioKeys = Object.keys(proposalList)?.filter(
    (key) => key !== '하우투약',
  )

  const handleShowPopup = (proposalId, status, createBy) => {
    const onlyView = !canEdit(createBy) && !isChief
    if (isChief) {
      setPopupMode('MASTER_VIEW')
    } else if (onlyView) {
      setPopupMode('MEMBER_VIEW')
    } else {
      setPopupMode('EDIT')
    }
    setSelectedApprovalId(proposalId)
    setShowDetailPopup(true)
    setSelectedApprovalStatus(status)
  }

  const handleDelete = async (proposalId) => {
    try {
      const res = await deleteApproval(proposalId)
      console.log('res::', res)
      Swal.fire({
        customClass: { popup: 'custom-alert' },
        icon: 'success',
        title: '삭제 완료',
        text: '기안서가 삭제되었습니다.',
        confirmButtonText: '확인',
        confirmButtonColor: '#117FFA',
      }).then(() => {
        refetchApprovalList()
      })
    } catch (error) {
      console.log('error::', error)
      return
    }
  }

  const returnWorkTypeText = (workType) => {
    switch (workType) {
      case 'CORE':
        return '필수 업무'
      case 'IMPROVEMENT':
        return '업무 개선'
      default:
        return workType
    }
  }

  const canEdit = (writerId) => {
    return writerId === user?.id
  }

  // 특정 스튜디오와 상태에 해당하는 제안서 필터링 및 정렬
  const getFilteredProposals = (studioName, statusKey) => {
    const filtered =
      proposalList[studioName]?.filter(
        (proposal) => proposal.status === statusKey,
      ) || []

    // 우선순위 정렬: isUrgent > workType == CORE > workType == IMPROVEMENT
    return filtered.sort((a, b) => {
      // 1. isUrgent 우선
      if (a.isUrgent !== b.isUrgent) {
        return a.isUrgent ? -1 : 1
      }

      // 2. workType 우선순위: CORE > IMPROVEMENT
      const workTypeOrder = { CORE: 1, IMPROVEMENT: 2 }
      const aOrder = workTypeOrder[a.workType] || 3
      const bOrder = workTypeOrder[b.workType] || 3

      return aOrder - bOrder
    })
  }

  // 제안서 카드 렌더링
  const renderProposalCard = (proposal) => (
    <div
      className="status-item yellow-border"
      key={`proposal-${proposal.id}`}
      onClick={() =>
        handleShowPopup(proposal.id, proposal.status, proposal.createdBy)
      }
    >
      <div className="item-top">
        <span className="item-tag">
          {returnWorkTypeText(proposal.workType)}
        </span>
        <Button
          style={{
            position: 'absolute',
            right: -20,
            top: 0,
            border: 'none',
          }}
          type="default"
          onClick={(e) => {
            e.stopPropagation()
            handleShowPopup(proposal.id, proposal.status, proposal.createdBy)
          }}
        >
          <RightOutlined />
        </Button>
        <div
          className={`color-chip color-chip-${proposal.workType.toLowerCase()}`}
        />
      </div>
      <Flex align="center" gap={6}>
        <Text className="item-title">{proposal.title}</Text>
        {proposal?.isUrgent && (
          <div className="item-priority-tag">우선 요청</div>
        )}
      </Flex>
      <Text className="item-desc">{proposal.workGoal}</Text>
      <div className="item-bottom">
        <Flex align="center" gap={2}>
          <img
            src={`${process.env.REACT_APP_HOST}/assets/icon/aops/calandar-check-icon.svg`}
            alt="calandar-check-icon"
            style={{ width: 16, height: 16 }}
          />
          <Text className="item-date">
            {dayjs(proposal.createdAt).format('YY년 MM월 DD일')}
          </Text>
        </Flex>
        <div className="item-bottom-divider" />
        <Flex align="center" gap={2}>
          <img
            src={`${process.env.REACT_APP_HOST}/assets/icon/aops/clock-icon.svg`}
            alt="calendar-check-icon"
            style={{ width: 16, height: 16 }}
          />
          <Text className="item-time">
            {dayjs(proposal.createdAt).format('HH:mm')}
          </Text>
        </Flex>
      </div>
    </div>
  )

  // 상태 컬럼 렌더링
  const renderStatusColumn = (studioName, status) => {
    const filteredProposals = getFilteredProposals(studioName, status.key)
    console.log('😻 filteredProposals::', filteredProposals)
    // 순서 터링
    // isUrgent > workType ==CORE > workType ==IMPROVEMENT
    return (
      <div className="status-column" key={`${studioName}-${status.key}`}>
        <div className="status-header">
          <Text className="status-title">
            {studioName} {status.label}
          </Text>
          <span className="status-count">{filteredProposals.length}</span>
        </div>
        <div className="status-items chief-status-items">
          {filteredProposals.length > 0 ? (
            filteredProposals.map(renderProposalCard)
          ) : (
            <Text className="item-desc">{status.emptyMessage}</Text>
          )}
        </div>
      </div>
    )
  }

  return (
    <section className="approval-status-section">
      <div className="status-column-group-chief">
        {statuses.map((status) => (
          <section key={status.key} className="studio-section">
            {studioKeys.map((studioName) =>
              renderStatusColumn(studioName, status),
            )}
          </section>
        ))}
      </div>
    </section>
  )
}

export default ApprovalProcessForChief
